import { useEffect, useState } from "react";
import type {
  MemberCommunicationDto,
  NotificationsResponseDto,
  TeacherScheduleResponseDto
} from "@madamuh/domain";
import { useNavigate, useParams } from "react-router-dom";
import { fetchTeacherMemberCommunications } from "../../shared/api/memberCommunication";
import { fetchTeacherNotifications } from "../../shared/api/notification";
import { fetchTeacherSchedule } from "../../shared/api/teacher";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { NotificationCard } from "../../shared/ui/NotificationCard";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";
import {
  buildTeacherPageLinks,
  TeacherAccessState,
  TeacherScheduleItemCard
} from "./teacherUi";
import { TeacherOccurrenceCommunicationPanel } from "./TeacherOccurrenceCommunicationPanel";
import { MemberCommunicationCard } from "../../shared/ui/MemberCommunicationCard";

async function fetchTeacherHomeData(tenantSlug: string) {
  const [nextSchedule, communicationsResult, notificationsResult] = await Promise.all([
    fetchTeacherSchedule(tenantSlug),
    fetchTeacherMemberCommunications(tenantSlug).then((value) => ({ status: "fulfilled" as const, value })).catch((reason) => ({
      status: "rejected" as const,
      reason
    })),
    fetchTeacherNotifications(tenantSlug).then((value) => ({ status: "fulfilled" as const, value })).catch((reason) => ({
      status: "rejected" as const,
      reason
    }))
  ]);

  return {
    schedule: nextSchedule,
    communications: communicationsResult.status === "fulfilled" ? communicationsResult.value : { items: [] as MemberCommunicationDto[] },
    notifications:
      notificationsResult.status === "fulfilled"
        ? notificationsResult.value
        : { items: [], prefs: null },
    hasPartialFailure: communicationsResult.status === "rejected" || notificationsResult.status === "rejected"
  };
}

export function TeacherHomePage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [schedule, setSchedule] = useState<TeacherScheduleResponseDto | null>(null);
  const [notifications, setNotifications] = useState<NotificationsResponseDto | null>(null);
  const [communications, setCommunications] = useState<{ items: MemberCommunicationDto[] } | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasTeacherRole = Boolean(session?.member?.roles.includes("teacher"));
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;
  const pageLinks = buildTeacherPageLinks(hasAdminRole, tenantLabels);
  const scheduleTimeZone = schedule?.timeZone ?? "Asia/Tokyo";
  const upcomingMonthGroups = schedule
    ? schedule.monthGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => item.occurrenceDate !== schedule.todayDate)
      }))
      .filter((group) => group.items.length > 0)
    : [];

  function applyHomeData(result: Awaited<ReturnType<typeof fetchTeacherHomeData>>) {
    setSchedule(result.schedule);
    setCommunications(result.communications);
    setNotifications(result.notifications);
    setLoadError(
      result.hasPartialFailure ? `一部の${tenantLabels.teacher}情報の取得に失敗しました。担当予定は表示しています。` : null
    );
    setLoadState("idle");
  }

  async function refreshHomeData() {
    const result = await fetchTeacherHomeData(tenantSlug);
    applyHomeData(result);
  }

  useEffect(() => {
    if (!isSessionReady || !hasTeacherRole) {
      return;
    }

    let cancelled = false;

    async function loadHome() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const result = await fetchTeacherHomeData(tenantSlug);

        if (cancelled) {
          return;
        }

        applyHomeData(result);
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, "teacher home の取得に失敗しました"));
      }
    }

    void loadHome();

    return () => {
      cancelled = true;
    };
  }, [hasTeacherRole, isSessionReady, navigate, tenantSlug]);

  return (
    <OperatorPageScaffold
      currentArea="teacher"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title={`${tenantLabels.teacher}ホーム`}
      description="今日の担当、近日の予定、通知、担当連絡を確認できます。"
      links={pageLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        <TeacherAccessState
          routeStatus={routeStatus}
          sessionStatus={sessionStatus}
          errorMessage={errorMessage}
          hasTeacherRole={hasTeacherRole}
          labels={tenantLabels}
        />

        {loadState === "loading" ? <p>{tenantLabels.teacher}ホームを読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {isSessionReady && hasTeacherRole ? (
          <>
            <section style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gap: 4 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>今日の担当</h2>
                <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                  今日の担当は当日中表示します。
                </p>
              </div>
              {schedule?.todayItems.length === 0 ? <p style={{ margin: 0 }}>本日の担当はありません。</p> : null}
              {schedule?.todayItems.map((item) => (
                <TeacherScheduleItemCard
                  key={item.occurrenceId}
                  item={item}
                  tenantSlug={tenantSlug}
                  timeZone={scheduleTimeZone}
                  showExpectedStudents
                  labels={tenantLabels}
                  actions={(
                    <TeacherOccurrenceCommunicationPanel
                      tenantSlug={tenantSlug}
                      item={item}
                      timeZone={scheduleTimeZone}
                      labels={tenantLabels}
                      onSent={refreshHomeData}
                    />
                  )}
                />
              ))}
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gap: 4 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>近日の予定</h2>
                <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                  今日から翌月末までの担当だけを表示します。
                </p>
              </div>
              {upcomingMonthGroups.length === 0 ? <p style={{ margin: 0 }}>近日の担当予定はありません。</p> : null}
              {upcomingMonthGroups.map((group) => (
                <div key={group.yearMonth} style={{ display: "grid", gap: 8 }}>
                  <h3 style={{ margin: 0, fontSize: 16 }}>{group.label}</h3>
                  <div style={{ display: "grid", gap: 8 }}>
                    {group.items.map((item) => (
                      <TeacherScheduleItemCard
                        key={item.occurrenceId}
                        item={item}
                        tenantSlug={tenantSlug}
                        timeZone={scheduleTimeZone}
                        showOccurrenceDate
                        labels={tenantLabels}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gap: 4 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>通知</h2>
                <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                  担当に関する通知を表示します。
                </p>
              </div>
              {notifications?.items.length === 0 ? <p style={{ margin: 0 }}>担当に関する通知はありません。</p> : null}
              {notifications?.items.map((item) => (
                <NotificationCard key={item.itemId} item={item} labels={tenantLabels} />
              ))}
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>担当連絡事項</h2>
              {communications?.items.length === 0 ? <p style={{ margin: 0 }}>担当予定への連絡事項はありません。</p> : null}
              {communications?.items.slice(0, 5).map((communication) => (
                <MemberCommunicationCard
                  key={communication.communicationId}
                  communication={communication}
                  tenantSlug={tenantSlug}
                  thumbnailSurface="teacher"
                  labels={tenantLabels}
                  compact
                />
              ))}
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}

export { TeacherAbsenceRequestPage } from "./TeacherAbsenceRequestPage";
export { TeacherOccurrenceDetailPage } from "./TeacherOccurrenceDetailPage";
